// SPDX-License-Identifier: AGPL-3.0-or-later

import {CUSTOM_SOUND_FORMATS, CUSTOM_SOUND_MAX_SIZE_LABEL} from '@app/features/app/config/I18nDisplayConstants';
import type * as CustomSoundDB from '@app/features/notification/utils/CustomSoundDB';
import type {SoundType} from '@app/features/notification/utils/SoundUtils';
import {Accordion} from '@app/features/ui/accordion/Accordion';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {Slider} from '@app/features/ui/components/Slider';
import {SwitchGroup, SwitchGroupCustomItem} from '@app/features/ui/components/SwitchGroup';
import type {SoundSettings} from '@app/features/ui/state/Sound';
import {formatRoundedPercentage, roundPercentage} from '@app/features/ui/utils/PercentageFormatting';
import styles from '@app/features/user/components/modals/tabs/notifications_tab/NotificationsTabSounds.module.css';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {ArrowCounterClockwiseIcon, SpeakerHighIcon, TrashIcon, UploadIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useRef, useState} from 'react';

const CONFIGURE_WHICH_SOUNDS_TO_PLAY_AND_WHEN_DESCRIPTOR = msg({
	message: 'Configure which sounds to play and when.',
	comment: 'Description text in the sounds.',
});
const MASTER_VOLUME_DESCRIPTOR = msg({
	message: 'Master volume',
	comment: 'Short label in the sounds. Keep it concise.',
});
const SETS_THE_LEVEL_FOR_EVERY_SOUND_EFFECT_PER_DESCRIPTOR = msg({
	message: 'Sets the level for every sound effect. Per-sound overrides ignore this.',
	comment: 'Description text in the sounds.',
});
const RESET_TO_DEFAULT_VOLUME_DESCRIPTOR = msg({
	message: 'Reset to default volume',
	comment: 'Button or menu action label in the sounds. Keep it concise.',
});
const DISABLE_ALL_NOTIFICATION_SOUNDS_DESCRIPTOR = msg({
	message: 'Disable all notification sounds',
	comment: 'Button or menu action label in the sounds. Keep it concise.',
});
const YOUR_EXISTING_NOTIFICATION_SOUND_SETTINGS_WILL_BE_PRESERVED_DESCRIPTOR = msg({
	message: 'Your existing notification sound settings will be preserved.',
	comment: 'Description text in the sounds.',
});
const CLICK_THE_UPLOAD_ICON_NEXT_TO_ANY_SOUND_DESCRIPTOR = msg({
	message:
		'Click the upload icon next to any sound to customize it with your own audio file. Supported formats: {customSoundFormats} (max {customSoundMaxSizeLabel}).',
	comment: 'Label in the sounds.',
});
const PREVIEW_SOUND_DESCRIPTOR = msg({
	message: 'Preview sound',
	comment: 'Short label in the sounds. Keep it concise.',
});
const UPLOAD_CUSTOM_SOUND_FOR_DESCRIPTOR = msg({
	message: 'Upload custom sound for {label}',
	comment: 'Button or menu action label in the sounds. Keep it concise. Preserve {label}; it is inserted by code.',
});
const REMOVE_CUSTOM_SOUND_FOR_DESCRIPTOR = msg({
	message: 'Remove custom sound for {label}',
	comment:
		'Button or menu action label in the sounds. Keep it concise. Preserve {label}; it is inserted by code. Keep the tone plain and specific.',
});
const ENABLE_ALL_DESCRIPTOR = msg({
	message: 'Enable all',
	comment: 'Button or menu action label in the sounds. Keep it concise.',
});
const DISABLE_ALL_DESCRIPTOR = msg({
	message: 'Disable all',
	comment: 'Button or menu action label in the sounds. Keep it concise.',
});
const PER_SOUND_VOLUME_DESCRIPTOR = msg({
	message: 'Per-sound volume',
	comment: 'Short label in the sounds. Keep it concise.',
});
const SOUND_S_OVERRIDE_THE_MASTER_VOLUME_DESCRIPTOR = msg({
	message: '{overrideCount} sound(s) override the master volume.',
	comment: 'Description text in the sounds. Preserve {overrideCount}; it is inserted by code.',
});
const SET_CUSTOM_VOLUMES_FOR_INDIVIDUAL_SOUNDS_SOUNDS_WITHOUT_DESCRIPTOR = msg({
	message: 'Set custom volumes for individual sounds. Sounds without an override follow the master volume.',
	comment: 'Description text in the sounds.',
});
const PREVIEW_DESCRIPTOR = msg({
	message: 'Preview {label}',
	comment: 'Short label in the sounds. Keep it concise. Preserve {label}; it is inserted by code.',
});
const FOLLOWING_MASTER_DESCRIPTOR = msg({
	message: 'Following master • {effectiveValue}%',
	comment: 'Label in the sounds. Preserve {effectiveValue}; it is inserted by code.',
});
const RESET_TO_MASTER_VOLUME_DESCRIPTOR = msg({
	message: 'Reset {label} to master volume',
	comment: 'Button or menu action label in the sounds. Keep it concise. Preserve {label}; it is inserted by code.',
});
const RESET_ALL_OVERRIDES_DESCRIPTOR = msg({
	message: 'Reset all overrides',
	comment: 'Button or menu action label in the sounds. Keep it concise.',
});

interface SoundsProps {
	soundSettings: SoundSettings;
	soundTypeLabels: Record<SoundType, string>;
	customSounds: Record<SoundType, CustomSoundDB.CustomSound | null>;
	isSoundEnabled: (soundType: SoundType) => boolean;
	onToggleAllSounds: (value: boolean) => void;
	onToggleSound: (soundType: SoundType, enabled: boolean) => void;
	onEnableAllSounds: () => void;
	onDisableAllSounds: () => void;
	onPreviewSound: (soundType: SoundType) => void;
	onUploadClick: (soundType: SoundType) => void;
	onCustomSoundDelete: (soundType: SoundType) => void;
	onMasterVolumeChange: (value: number) => void;
	onSoundOverrideChange: (soundType: SoundType, value: number) => void;
	onSoundOverrideReset: (soundType: SoundType) => void;
	onAllOverridesReset: () => void;
}

const DEFAULT_MASTER = 100;
export const Sounds: React.FC<SoundsProps> = observer(
	({
		soundSettings,
		soundTypeLabels,
		customSounds,
		isSoundEnabled,
		onToggleAllSounds,
		onToggleSound,
		onEnableAllSounds,
		onDisableAllSounds,
		onPreviewSound,
		onUploadClick,
		onCustomSoundDelete,
		onMasterVolumeChange,
		onSoundOverrideChange,
		onSoundOverrideReset,
		onAllOverridesReset,
	}) => {
		const {i18n} = useLingui();
		const masterVolume = soundSettings.masterVolume ?? DEFAULT_MASTER;
		const soundOverrides = soundSettings.soundOverrides ?? {};
		const overrideCount = Object.keys(soundOverrides).length;
		const hasAnyOverride = overrideCount > 0;
		const allDisabled = soundSettings.allSoundsDisabled;
		const [accordionExpanded, setAccordionExpanded] = useState(hasAnyOverride);
		const previousHadOverridesRef = useRef(hasAnyOverride);
		useEffect(() => {
			if (hasAnyOverride && !previousHadOverridesRef.current) {
				setAccordionExpanded(true);
			}
			previousHadOverridesRef.current = hasAnyOverride;
		}, [hasAnyOverride]);
		return (
			<div className={styles.container} data-flx="user.notifications-tab.sounds.container">
				<p className={styles.description} data-flx="user.notifications-tab.sounds.description">
					{i18n._(CONFIGURE_WHICH_SOUNDS_TO_PLAY_AND_WHEN_DESCRIPTOR)}
				</p>
				<div className={styles.content} data-flx="user.notifications-tab.sounds.content">
					<div className={styles.masterRow} data-flx="user.notifications-tab.sounds.master-row">
						<div className={styles.masterHeader} data-flx="user.notifications-tab.sounds.master-header">
							<span className={styles.masterLabel} data-flx="user.notifications-tab.sounds.master-label">
								{i18n._(MASTER_VOLUME_DESCRIPTOR)}
							</span>
							<span className={styles.masterValue} data-flx="user.notifications-tab.sounds.master-value">
								{formatRoundedPercentage(masterVolume)}
							</span>
						</div>
						<p className={styles.masterDescription} data-flx="user.notifications-tab.sounds.master-description">
							{i18n._(SETS_THE_LEVEL_FOR_EVERY_SOUND_EFFECT_PER_DESCRIPTOR)}
						</p>
						<Slider
							value={masterVolume}
							defaultValue={masterVolume}
							factoryDefaultValue={DEFAULT_MASTER}
							minValue={0}
							maxValue={200}
							step={1}
							markers={[0, 50, 100, 150, 200]}
							disabled={allDisabled}
							onMarkerRender={formatRoundedPercentage}
							onValueRender={formatRoundedPercentage}
							onValueChange={onMasterVolumeChange}
							showResetButton={true}
							resetTooltip={i18n._(RESET_TO_DEFAULT_VOLUME_DESCRIPTOR)}
							data-flx="user.notifications-tab.sounds.slider"
						/>
					</div>
					<Switch
						label={i18n._(DISABLE_ALL_NOTIFICATION_SOUNDS_DESCRIPTOR)}
						description={i18n._(YOUR_EXISTING_NOTIFICATION_SOUND_SETTINGS_WILL_BE_PRESERVED_DESCRIPTOR)}
						value={allDisabled}
						onChange={onToggleAllSounds}
						data-flx="user.notifications-tab.sounds.switch.toggle-all-sounds"
					/>
					<div className={styles.hint} data-flx="user.notifications-tab.sounds.hint">
						{i18n._(CLICK_THE_UPLOAD_ICON_NEXT_TO_ANY_SOUND_DESCRIPTOR, {
							customSoundFormats: CUSTOM_SOUND_FORMATS,
							customSoundMaxSizeLabel: CUSTOM_SOUND_MAX_SIZE_LABEL,
						})}
					</div>
					<SwitchGroup data-flx="user.notifications-tab.sounds.switch-group">
						{Object.entries(soundTypeLabels).map(([soundType, label]) => {
							const type = soundType as SoundType;
							return (
								<SwitchGroupCustomItem
									key={soundType}
									label={
										<div className={styles.soundLabel} data-flx="user.notifications-tab.sounds.sound-label">
											<div
												className={styles.soundLabelTitle}
												data-flx="user.notifications-tab.sounds.sound-label-title"
											>
												<span data-flx="user.notifications-tab.sounds.span">{label}</span>
												{customSounds[type] && (
													<span className={styles.customBadge} data-flx="user.notifications-tab.sounds.custom-badge">
														<Trans>Custom</Trans>
													</span>
												)}
											</div>
											<button
												type="button"
												className={styles.previewLink}
												onClick={() => onPreviewSound(type)}
												disabled={allDisabled}
												data-flx="user.notifications-tab.sounds.preview-link.preview-sound.button"
											>
												{i18n._(PREVIEW_SOUND_DESCRIPTOR)}
											</button>
										</div>
									}
									value={allDisabled ? false : isSoundEnabled(type)}
									disabled={allDisabled}
									onChange={(enabled) => onToggleSound(type, enabled)}
									extraContent={
										<>
											<button
												type="button"
												onClick={() => onUploadClick(type)}
												disabled={allDisabled}
												className={styles.iconButton}
												aria-label={i18n._(UPLOAD_CUSTOM_SOUND_FOR_DESCRIPTOR, {label})}
												data-flx="user.notifications-tab.sounds.icon-button.upload-click"
											>
												<UploadIcon
													size={16}
													className={styles.uploadIcon}
													data-flx="user.notifications-tab.sounds.upload-icon"
												/>
											</button>
											{customSounds[type] && (
												<button
													type="button"
													onClick={() => onCustomSoundDelete(type)}
													disabled={allDisabled}
													className={styles.iconButton}
													aria-label={i18n._(REMOVE_CUSTOM_SOUND_FOR_DESCRIPTOR, {label})}
													data-flx="user.notifications-tab.sounds.icon-button.custom-sound-delete"
												>
													<TrashIcon
														size={16}
														className={styles.deleteIcon}
														data-flx="user.notifications-tab.sounds.delete-icon"
													/>
												</button>
											)}
										</>
									}
									data-flx="user.notifications-tab.sounds.switch-group-custom-item.toggle-sound"
								/>
							);
						})}
					</SwitchGroup>
					<div className={styles.actionsContainer} data-flx="user.notifications-tab.sounds.actions-container">
						<button
							type="button"
							className={styles.actionButton}
							onClick={onEnableAllSounds}
							disabled={allDisabled}
							data-flx="user.notifications-tab.sounds.action-button.enable-all-sounds"
						>
							{i18n._(ENABLE_ALL_DESCRIPTOR)}
						</button>
						<span className={styles.actionSeparator} data-flx="user.notifications-tab.sounds.action-separator">
							•
						</span>
						<button
							type="button"
							className={styles.actionButton}
							onClick={onDisableAllSounds}
							disabled={allDisabled}
							data-flx="user.notifications-tab.sounds.action-button.disable-all-sounds"
						>
							{i18n._(DISABLE_ALL_DESCRIPTOR)}
						</button>
					</div>
					<Accordion
						id="sound-overrides"
						title={i18n._(PER_SOUND_VOLUME_DESCRIPTOR)}
						description={
							hasAnyOverride
								? i18n._(SOUND_S_OVERRIDE_THE_MASTER_VOLUME_DESCRIPTOR, {overrideCount})
								: i18n._(SET_CUSTOM_VOLUMES_FOR_INDIVIDUAL_SOUNDS_SOUNDS_WITHOUT_DESCRIPTOR)
						}
						expanded={accordionExpanded}
						onExpandedChange={setAccordionExpanded}
						data-flx="user.notifications-tab.sounds.sound-overrides"
					>
						<div className={styles.overridesList} data-flx="user.notifications-tab.sounds.overrides-list">
							{Object.entries(soundTypeLabels).map(([soundType, label]) => {
								const type = soundType as SoundType;
								const override = soundOverrides[type];
								const hasOverride = override !== undefined;
								const effectiveValue = hasOverride ? (override as number) : masterVolume;
								const effectiveValueLabel = formatRoundedPercentage(effectiveValue);
								const rowDisabled = allDisabled || !isSoundEnabled(type);
								return (
									<div
										key={soundType}
										className={clsx(styles.overrideRow, !hasOverride && styles.overrideRowLinked)}
										data-flx="user.notifications-tab.sounds.override-row"
									>
										<div className={styles.overrideHeader} data-flx="user.notifications-tab.sounds.override-header">
											<span className={styles.overrideLabel} data-flx="user.notifications-tab.sounds.override-label">
												{label}
											</span>
											<button
												type="button"
												className={styles.previewButton}
												onClick={() => onPreviewSound(type)}
												aria-label={i18n._(PREVIEW_DESCRIPTOR, {label})}
												data-flx="user.notifications-tab.sounds.preview-button.preview-sound"
											>
												<SpeakerHighIcon
													size={14}
													weight="fill"
													data-flx="user.notifications-tab.sounds.speaker-high-icon"
												/>
											</button>
											<span className={styles.overrideStatus} data-flx="user.notifications-tab.sounds.override-status">
												{hasOverride
													? effectiveValueLabel
													: i18n._(FOLLOWING_MASTER_DESCRIPTOR, {effectiveValue: roundPercentage(effectiveValue)})}
											</span>
											<button
												type="button"
												className={styles.resetButton}
												onClick={() => onSoundOverrideReset(type)}
												disabled={rowDisabled || !hasOverride}
												aria-label={i18n._(RESET_TO_MASTER_VOLUME_DESCRIPTOR, {label})}
												data-flx="user.notifications-tab.sounds.reset-button.sound-override-reset"
											>
												<ArrowCounterClockwiseIcon
													size={14}
													weight="bold"
													data-flx="user.notifications-tab.sounds.arrow-counter-clockwise-icon"
												/>
											</button>
										</div>
										<Slider
											value={effectiveValue}
											defaultValue={effectiveValue}
											factoryDefaultValue={masterVolume}
											minValue={0}
											maxValue={200}
											step={1}
											disabled={rowDisabled}
											onValueRender={formatRoundedPercentage}
											onValueChange={(value) => onSoundOverrideChange(type, value)}
											data-flx="user.notifications-tab.sounds.slider--2"
										/>
									</div>
								);
							})}
						</div>
						{hasAnyOverride && (
							<div className={styles.actionsContainer} data-flx="user.notifications-tab.sounds.actions-container--2">
								<button
									type="button"
									className={styles.actionButton}
									onClick={onAllOverridesReset}
									disabled={allDisabled}
									data-flx="user.notifications-tab.sounds.action-button.all-overrides-reset"
								>
									{i18n._(RESET_ALL_OVERRIDES_DESCRIPTOR)}
								</button>
							</div>
						)}
					</Accordion>
				</div>
			</div>
		);
	},
);
